import * as z from 'zod/v4';

type LanguagePriority = 'user' | 'application';

const FeatureFlagsSchema = z.object({
  enableThemeToggle: z.boolean({
    error: 'Provide the variable NEXT_PUBLIC_ENABLE_THEME_TOGGLE',
  }),
  enableAccountDeletion: z.boolean({
    error: 'Provide the variable NEXT_PUBLIC_ENABLE_PERSONAL_ACCOUNT_DELETION',
  }),
  enableTeamDeletion: z.boolean({
    error: 'Provide the variable NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_DELETION',
  }),
  enableTeamAccounts: z.boolean({
    error: 'Provide the variable NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS',
  }),
  enableTeamCreation: z.boolean({
    error: 'Provide the variable NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_CREATION',
  }),
  enablePersonalAccountBilling: z.boolean({
    error: 'Provide the variable NEXT_PUBLIC_ENABLE_PERSONAL_ACCOUNT_BILLING',
  }),
  enableTeamAccountBilling: z.boolean({
    error: 'Provide the variable NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_BILLING',
  }),
  languagePriority: z
    .enum(['user', 'application'], {
      error: 'Provide the variable NEXT_PUBLIC_LANGUAGE_PRIORITY',
    })
    .default('application'),
  enableNotifications: z.boolean({
    error: 'Provide the variable NEXT_PUBLIC_ENABLE_NOTIFICATIONS',
  }),
  realtimeNotifications: z.boolean({
    error: 'Provide the variable NEXT_PUBLIC_REALTIME_NOTIFICATIONS',
  }),
  enableVersionUpdater: z.boolean({
    error: 'Provide the variable NEXT_PUBLIC_ENABLE_VERSION_UPDATER',
  }),
  enableAiAutofill: z.boolean({
    error: 'Provide the variable NEXT_PUBLIC_ENABLE_AI_AUTOFILL',
  }),
  enableMiloSubmissions: z.boolean({
    error: 'Provide the variable NEXT_PUBLIC_ENABLE_MILO_SUBMISSIONS',
  }),
  enableExternalFormAccess: z.boolean({
    error: 'Provide the variable NEXT_PUBLIC_FT_EXTERNAL_FORM_ACCESS',
  }),
  enableEmployeePass: z.boolean({
    error: 'Provide the variable NEXT_PUBLIC_FT_EMPLOYEE_PASS',
  }),
  enableSupplierCorpusPrefill: z.boolean({
    error: 'Provide the variable NEXT_PUBLIC_ENABLE_SUPPLIER_CORPUS_PREFILL',
  }),
  enableRowScoping: z.boolean({
    error: 'Provide the variable NEXT_PUBLIC_FT_ROW_SCOPING',
  }),
  enableCheckInOutSetting: z.boolean({
    error: 'Provide the variable NEXT_PUBLIC_FT_CHECK_IN_OUT',
  }),
  enableAutoClock: z.boolean({
    error: 'Provide the variable NEXT_PUBLIC_FT_AUTO_CLOCK',
  }),
  enableOfflinePrefetchSetting: z.boolean({
    error: 'Provide the variable NEXT_PUBLIC_FT_OFFLINE_PREFETCH',
  }),
  enableFinancialSensitivity: z.boolean({
    error: 'Provide the variable NEXT_PUBLIC_FT_FINANCIAL_SENSITIVITY',
  }),
  enableDailyRecord: z.boolean({
    error: 'Provide the variable NEXT_PUBLIC_FT_DAILY_RECORD',
  }),
  enableDailyRecordCharges: z.boolean({
    error: 'Provide the variable NEXT_PUBLIC_FT_DAILY_RECORD_CHARGES',
  }),
  enableUpdateCelebrations: z.boolean({
    error: 'Provide the variable NEXT_PUBLIC_ENABLE_UPDATE_CELEBRATIONS',
  }),
  enableGcCommercial: z.boolean({
    error: 'Provide the variable NEXT_PUBLIC_FT_GC_COMMERCIAL',
  }),
  enableRecordSignOff: z.boolean({
    error: 'Provide the variable NEXT_PUBLIC_FT_RECORD_SIGN_OFF',
  }),
});

const featuresFlagConfig = FeatureFlagsSchema.parse({
  enableThemeToggle: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_THEME_TOGGLE,
    true,
  ),
  enableAccountDeletion: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_PERSONAL_ACCOUNT_DELETION,
    false,
  ),
  enableTeamDeletion: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_DELETION,
    false,
  ),
  enableTeamAccounts: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS,
    true,
  ),
  enableTeamCreation: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_CREATION,
    true,
  ),
  enablePersonalAccountBilling: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_PERSONAL_ACCOUNT_BILLING,
    false,
  ),
  enableTeamAccountBilling: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_TEAM_ACCOUNTS_BILLING,
    false,
  ),
  languagePriority: process.env
    .NEXT_PUBLIC_LANGUAGE_PRIORITY as LanguagePriority,
  enableNotifications: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_NOTIFICATIONS,
    true,
  ),
  realtimeNotifications: getBoolean(
    process.env.NEXT_PUBLIC_REALTIME_NOTIFICATIONS,
    false,
  ),
  enableVersionUpdater: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_VERSION_UPDATER,
    false,
  ),
  // "AI Autofill" (voice prefill) — default OFF; enabled per-environment
  // (staging) only. Parsed with `getLenientFlag` (NOT `getBoolean`) so this
  // server gate accepts the SAME env spellings as the builder toggle's read
  // (`readEnvFlag` in packages/features/forms/src/lib/feature-flags.ts) —
  // otherwise `1`/`on`/`yes`/`TRUE` would show the toggle while every prefill
  // call 403'd. Scoped to this flag only so other flags keep strict semantics
  // (Claudio + Cursor Bugbot review).
  enableAiAutofill: getLenientFlag(
    process.env.NEXT_PUBLIC_ENABLE_AI_AUTOFILL,
    false,
  ),
  // Milo "Ask your data" — the mobile-callable submissions Q&A route. Default
  // OFF; enabled per-environment (staging first). Parsed leniently (same as
  // enableAiAutofill) so the server gate accepts `1`/`on`/`yes`/`TRUE` too.
  enableMiloSubmissions: getLenientFlag(
    process.env.NEXT_PUBLIC_ENABLE_MILO_SUBMISSIONS,
    false,
  ),
  // Asset/Project QR "External Access" (linked forms + QR generation + the
  // public scan flow). Default OFF so it can ship dark and be enabled per
  // environment (staging first) for testing even after the code is released.
  // Web-only: QR codes are only generated on web, so mobile needs no flag.
  enableExternalFormAccess: getBoolean(
    process.env.NEXT_PUBLIC_FT_EXTERNAL_FORM_ACCESS,
    false,
  ),
  // Employee wallet pass issuance (PAY-2088) — the Apple `.pkpass` download and
  // the Google Wallet save link. Default OFF so it ships dark and is enabled
  // per environment once signing material is provisioned; the server check in
  // resolveEmployeePassContext is authoritative.
  enableEmployeePass: getBoolean(
    process.env.NEXT_PUBLIC_FT_EMPLOYEE_PASS,
    false,
  ),
  // Supplier Marketplace Corpus Prefill (PAY-2359) — prefilling draft offerings on onboarding.
  // Default OFF so it ships dark and is enabled per environment (staging first); aligned with DB gate.
  enableSupplierCorpusPrefill: getLenientFlag(
    process.env.NEXT_PUBLIC_ENABLE_SUPPLIER_CORPUS_PREFILL,
    false,
  ),
  // PAY-2171 — the Supplier Marketplace + global Supplier Portal POC has NO
  // environment flag on purpose: it is switched on per ACCOUNT with the
  // `SUPPLIER_MARKETPLACE` feature grant (super admin, admin portal → account →
  // features). See `apps/web/lib/supplier-marketplace/gate.ts` for the
  // authoritative checks and `.../feature.ts` for the settings-derived
  // visibility predicates.
  // Row scoping (self / designated / all + designated-worker crews — the
  // PAY-1607 mechanism managed by PAY-1606's member editor). Default OFF so
  // it ships dark: scopes have NO enforcement effect until PAY-1588 wires
  // them into RLS, and showing dead config UI is pointless. Gates BOTH the
  // editor surface (scope selectors + crew picker + draft sections) and the
  // server action's scope/crew save sections; flip per environment once
  // PAY-1588 lands.
  enableRowScoping: getBoolean(process.env.NEXT_PUBLIC_FT_ROW_SCOPING, false),
  // Check IN/OUT tenant setting (PAY-2565). Default OFF, and the reason is
  // release ORDERING rather than readiness: the setting only decides whether
  // Assignar Pay MOBILE offers check in/out (PAY-2566), and web can merge well
  // ahead of the app shipping. Without this flag an owner could switch the
  // feature on for their account and no device would have the build that
  // honours it — the toggle would look broken rather than pending.
  //
  // Turn it on per environment once a mobile build carrying PAY-2566 is out.
  // Purely a VISIBILITY gate on the settings card: it does not touch the stored
  // setting, so an account that already opted in keeps its value and mobile
  // keeps reading it either way.
  enableCheckInOutSetting: getBoolean(
    process.env.NEXT_PUBLIC_FT_CHECK_IN_OUT,
    false,
  ),
  // Automatic check in/out configuration (PAY-1620). Default OFF because no
  // device honours it yet. Visibility only: the setting remains stored and
  // readable by Pay Mobile while this group is hidden.
  enableAutoClock: getBoolean(process.env.NEXT_PUBLIC_FT_AUTO_CLOCK, false),
  // Offline prefetch window (PAY-2642). Default OFF, and — like the check
  // in/out toggle above — the reason is release ORDERING, not readiness: the
  // setting only decides how many days Assignar Pay MOBILE pre-downloads
  // (PAY-2643), and web can merge well ahead of the app shipping. Without this
  // flag an admin could widen the window to a fortnight in production and no
  // device would have the build that honours it — the card would look broken
  // rather than pending.
  //
  // Turn it on per environment once a mobile build carrying PAY-2643 is out.
  // Purely a VISIBILITY gate on the settings card: it does not touch the stored
  // setting, so an account that already saved a window keeps its value and
  // mobile keeps reading it either way. The stored default {1, 0} reproduces
  // today's behaviour exactly, so an account that never opens the card is
  // unaffected whichever way this flag sits.
  enableOfflinePrefetchSetting: getBoolean(
    process.env.NEXT_PUBLIC_FT_OFFLINE_PREFETCH,
    false,
  ),
  // Financial sensitivity classes (PAY-2435) — the cost/sell column-visibility
  // dimension. Default OFF.
  //
  // ⚠️ READ THIS BEFORE ASSUMING WHAT THE FLAG DOES. It gates only WHO CAN
  // ASSIGN a class: the two checkboxes in the access-preset / member editors,
  // and (authoritatively) the server-side prune in the member-access action.
  // ENFORCEMENT IS NOT GATED. Masking, redaction, export omission and the
  // guards stay on regardless — with the flag off nobody can create a
  // restricted member, so enforcement is inert in practice yet cannot be
  // bypassed by a crafted RPC or a psql assignment.
  //
  // Consequently this flag is NOT the rollback switch. Rollback is re-granting
  // both classes to every role in `role_permissions`, which turns every
  // masking path into a provable pass-through (pinned by the identity test).
  enableFinancialSensitivity: getBoolean(
    process.env.NEXT_PUBLIC_FT_FINANCIAL_SENSITIVITY,
    false,
  ),
  // Daily Record (PAY-3451) — the supervisor's Site Diary capture surface
  // (PAY-2904) and the record work that builds on it. Default OFF.
  //
  // One of TWO gates, and both must pass: this environment flag says the code
  // is live in this environment, and the per-account `DAILY_RECORD` feature
  // grant (admin portal) switches it on for one tenant at a time, so the first
  // accounts go live while everyone else keeps today's screens. Nothing turns
  // on in production until Daily Record 8 has reshaped the existing dockets.
  //
  // A ROLLOUT gate, not a security boundary: RLS and the processor's per-row
  // checks remain the boundary. The database checks the grant itself
  // (`account_has_feature(…, 'daily_record')`), because this flag is not
  // visible there.
  enableDailyRecord: getBoolean(process.env.NEXT_PUBLIC_FT_DAILY_RECORD, false),
  // Daily Record charges (PAY-5349): one switch for the billing layer of
  // Daily Record, which used to ship behind four flags. It covers question
  // charge rows (PAY-3458: a record's charge-bearing answers and imported Ops
  // docket lines as their own rows in ap_record_charges), T&M marking
  // (PAY-3462: the supervisor decides which rows are charged), the Rate-Based
  // Work tab (PAY-3470) and real runs of the reshape-legacy-dockets task
  // (PAY-3473; a dry run needs no flag). Default OFF, strict.
  //
  // One of TWO gates, like enableDailyRecord: this flag says the code is live
  // in this environment, and the per-account `DAILY_RECORD_CHARGES` grant
  // (with `DAILY_RECORD`) switches it on for one tenant at a time. Read it
  // through `isDailyRecordChargesEnabled`. A ROLLOUT gate, not a security
  // boundary: the database checks both grants itself
  // (`pay_record_charges_enabled`), and Pay Mobile reads only the grants.
  enableDailyRecordCharges: getBoolean(
    process.env.NEXT_PUBLIC_FT_DAILY_RECORD_CHARGES,
    false,
  ),
  // Release celebrations (PAY-4392): the "Assignar has been updated."
  // notice, its poem and its animation, once per signed-in user and release.
  // On by default from the merge that ships it. A signed-out visitor never
  // claims. The first claim on a track is a silent baseline, so the release
  // that introduces the feature does not celebrate itself.
  //
  // NEXT_PUBLIC_ENABLE_UPDATE_CELEBRATIONS=false skips the claim and receipts
  // nothing. The "Show update notices" setting is the per-person control, and
  // it also governs Pay Mobile.
  //
  // Not a security boundary: the claim RPC is auth.uid()-bound.
  enableUpdateCelebrations: getBoolean(
    process.env.NEXT_PUBLIC_ENABLE_UPDATE_CELEBRATIONS,
    true,
  ),
  // PAY-5036. A rollout gate, not a security boundary; read only through
  // `isGcCommercialEnabled()`. Flag on shows the hub surfaces only to accounts
  // with the Subcontractor Hub grant, and the hire-and-claims surfaces only to
  // accounts with the hire-and-claims grant.
  enableGcCommercial: getBoolean(
    process.env.NEXT_PUBLIC_FT_GC_COMMERCIAL,
    false,
  ),
  // Record sign-off (PAY-3465, Daily Record 3): the record's lifecycle on its
  // screens (send for signature, return a disputed record to draft) and the
  // template's "Requires a client signature" setting. Default OFF, strict.
  //
  // One of TWO gates with the account's DAILY_RECORD grant; every server
  // action checks both. The database cannot see this flag: its gate is the
  // template's stored `dailyRecordSignOff`, which only the builder writes and
  // only while this flag is on (the form actions keep the stored value when
  // it is off). A template without the key behaves exactly as today.
  enableRecordSignOff: getBoolean(
    process.env.NEXT_PUBLIC_FT_RECORD_SIGN_OFF,
    false,
  ),
} satisfies z.output<typeof FeatureFlagsSchema>);

export default featuresFlagConfig;

function getBoolean(value: unknown, defaultValue: boolean) {
  if (typeof value === 'string') {
    return value === 'true';
  }

  return defaultValue;
}

/**
 * Lenient env-flag parser mirroring `readEnvFlag` in
 * packages/features/forms/src/lib/feature-flags.ts (trimmed + lowercased,
 * accepts `0/false/off/no` and `1/true/on/yes`). Used ONLY where the same env
 * var is also read by that package helper, so the two reads can't disagree.
 * The default `getBoolean` above stays strict (`=== 'true'`) to avoid widening
 * truthiness for unrelated flags.
 */
function getLenientFlag(value: unknown, defaultValue: boolean) {
  if (typeof value !== 'string') return defaultValue;

  const normalized = value.trim().toLowerCase();
  if (normalized === '') return defaultValue;
  if (['0', 'false', 'off', 'no'].includes(normalized)) return false;
  if (['1', 'true', 'on', 'yes'].includes(normalized)) return true;

  return defaultValue;
}
